import { STEP_KINDS } from './rounds';
import { paintStrokes } from './raster';
import { isBlankPage } from './game';

// One book as one tall PNG. Bug report (2026-09-11): "It would be fun if we
// could save the books afterwards." Drawings are rasterised through the same
// painter as everything else; text pages get a card of the same width so the
// chain reads top to bottom the way the reveal showed it.

export const BOOK_WIDTH = 900;
const PAD = 40;
const ART = BOOK_WIDTH - PAD * 2;
const HEAD = 110;
const TEXT_CARD = 190;
const BYLINE = 34;

const verbFor = (kind) => ({ word: 'started with', draw: 'drew', guess: 'guessed' }[kind] || 'wrote');

const wrap = (ctx, text, maxWidth) => {
  const words = String(text).split(/\s+/);
  const lines = [];
  let line = '';
  for (const word of words) {
    const probe = line ? `${line} ${word}` : word;
    if (ctx.measureText(probe).width > maxWidth && line) { lines.push(line); line = word; } else line = probe;
  }
  if (line) lines.push(line);
  return lines.slice(0, 3);
};

const pageHeight = (page) => BYLINE + (page.kind === STEP_KINDS.DRAW ? ART : TEXT_CARD) + PAD;

export const bookHeight = (book) => HEAD + (book.pages || []).reduce((h, p) => h + pageHeight(p), 0) + PAD;

/**
 * Paints `book` onto `canvas` (sized here) and returns it. Pure drawing; the
 * caller decides what to do with the pixels.
 */
export const paintBook = (canvas, book, { title } = {}) => {
  const pages = book.pages || [];
  canvas.width = BOOK_WIDTH;
  canvas.height = bookHeight(book);
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.fillStyle = '#1d1828';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.fillStyle = '#f3f0f8';
  ctx.font = '800 40px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
  ctx.textBaseline = 'top';
  ctx.fillText(`${book.ownerName}'s book`, PAD, PAD);
  if (title) {
    ctx.fillStyle = '#b9aecd';
    ctx.font = '600 20px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
    ctx.fillText(title, PAD, PAD + 52);
  }

  let y = HEAD;
  pages.forEach((page) => {
    ctx.fillStyle = '#b9aecd';
    ctx.font = '600 20px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
    ctx.fillText(`${page.byName || 'Someone'} ${verbFor(page.kind)}`, PAD, y);
    y += BYLINE;

    if (page.kind === STEP_KINDS.DRAW) {
      // Paint at art size on a scratch canvas so the painter's unit maths
      // stays square, then blit into place.
      const art = document.createElement('canvas');
      art.width = ART;
      art.height = ART;
      paintStrokes(art.getContext('2d'), isBlankPage(page) ? [] : page.content, ART);
      ctx.drawImage(art, PAD, y);
      y += ART;
    } else {
      ctx.fillStyle = '#322a42';
      ctx.fillRect(PAD, y, ART, TEXT_CARD);
      const blank = isBlankPage(page);
      ctx.fillStyle = blank ? '#b9aecd' : '#f3f0f8';
      ctx.font = `${blank ? 'italic 500 28px' : '800 44px'} system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
      const lines = wrap(ctx, blank ? '(left blank)' : page.content, ART - PAD * 2);
      const lineHeight = blank ? 36 : 54;
      let ty = y + (TEXT_CARD - lines.length * lineHeight) / 2;
      lines.forEach((line) => { ctx.fillText(line, PAD * 2, ty); ty += lineHeight; });
      y += TEXT_CARD;
    }
    y += PAD;
  });
  return canvas;
};

const safeName = (s) => String(s || 'book').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase();

/**
 * Renders and hands the PNG to the person: the share sheet where it can take
 * files (that's how a phone gets it into Photos), a download otherwise.
 */
export const saveBook = async (book, { title } = {}) => {
  const canvas = paintBook(document.createElement('canvas'), book, { title });
  if (!canvas) return false;
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) return false;
  const file = new File([blob], `telestrations-${safeName(book.ownerName)}.png`, { type: 'image/png' });
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: `${book.ownerName}'s book` });
      return true;
    } catch (err) {
      if (err?.name === 'AbortError') return false;
      // Fall through to a download.
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  document.body.append(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10000);
  return true;
};
